import { useRepairOrderQuotesStore } from '../../../repair-order-quotes-context';
import { useAvailabilityStore } from '../availability-store';
import styles from './styles.module.scss';
import { StockPart } from 'api-new/generated';
import clsx from 'clsx';
import { InlineSvg } from 'components/svg';
import { useAppStore } from 'features/settings/contexts/settings.context';
import { usePartRequestsPartsLookupQuery } from 'features/vhc/api/use-part-pequests-parts-lookup.query';
import { usePartsBranchAvailabilityMutation } from 'features/vhc/api/use-parts-branch-availability.mutation';
import { RepairOrderQuoteItems } from 'features/vhc/pages/record/form-state';
import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

type Props = {
  mainIndex: number;
  rowIndex: number;
  isShow: boolean;
};
type BranchState = {
  branchName: string;
  branchCount: number;
  isNotFoundInCDK: boolean;
};

export const BranchesComponent: FC<Props> = ({ mainIndex, rowIndex }) => {
  const { availabilityState, setAvailabilityState } = useAvailabilityStore();
  const { isEditMode, dataState } = useRepairOrderQuotesStore();
  const [branchState, setBranchState] = useState<BranchState[]>([]);
  const {
    state: { branches }
  } = useAppStore();

  const { watch } = useFormContext<RepairOrderQuoteItems>();
  const isDeleted = watch(`items.${mainIndex}.parts.${rowIndex}.isDeleted`);
  const brandCode = watch(`items.${mainIndex}.parts.${rowIndex}.brandCode`);
  const partNumber = watch(`items.${mainIndex}.parts.${rowIndex}.partNumber`);
  const isLoadedPartInfo = useRef(false);
  const { isLoading: isLoadingBranch, mutate: mutateBranch } = usePartsBranchAvailabilityMutation();

  const { refetch: refetchPartRequestsPartsCheckOnStock, isFetching: isLoadingPartsLookup } =
    usePartRequestsPartsLookupQuery(
      {
        brandCode: brandCode ?? undefined,
        partNumber: partNumber?.replace(/^0+(?!$)/, '')
      },
      [mainIndex, rowIndex],
      {
        enabled: false,
        onSuccess: (data) => {
          isLoadedPartInfo.current = true;

          setAvailabilityState({
            mainIndex: mainIndex,
            rowIndex: rowIndex,
            availability: data[0].availability
          });

          const wipInfoId = watch(`items.${mainIndex}.parts.${rowIndex}.wipInfoId`);

          if (wipInfoId) {
            const result: StockPart[] = branches.map((branch) => {
              if (data[0].availability?.length) {
                const matched = data[0]?.availability?.find((x) => x.branch === branch.value);

                return {
                  branch: branch.value,
                  branchName: branch.label,
                  branchCount: matched?.availableStock ?? 0,
                  isNotFoundInCDK: matched?.isNotFoundInCDK ?? false
                };
              }

              return {
                branch: branch.value,
                branchName: branch.label,
                branchCount: 0,
                isNotFoundInCDK: false
              };
            });

            mutateBranch({
              id: wipInfoId,
              requestBody: result
            });
          }
        }
      }
    );

  const isEnable = !!brandCode && !!partNumber;

  useEffect(() => {
    if (!branchState.length && branches?.length) {
      const result: BranchState[] = branches.map((branch) => ({
        branchName: branch.label,
        branchCount: 0,
        isNotFoundInCDK: false
      }));

      setBranchState(result);
    }
  }, [branchState.length, branches]);

  useEffect(() => {
    const result: BranchState[] = branches.map((branch) => {
      if (
        availabilityState &&
        availabilityState.mainIndex === mainIndex &&
        availabilityState.rowIndex === rowIndex &&
        branches?.length
      ) {
        const matched = availabilityState.availability?.find((x) => x.branch === branch.value);

        return {
          branchName: branch.label,
          branchCount: matched?.availableStock ?? 0,
          isNotFoundInCDK: matched?.isNotFoundInCDK ?? false
        };
      }

      if (
        dataState?.length &&
        dataState?.[mainIndex]?.parts?.length &&
        dataState?.[mainIndex]?.parts?.[rowIndex]?.stockParts
      ) {
        const matched = dataState?.[mainIndex]?.parts?.[rowIndex].stockParts?.find((x) => x.branch === branch.value);

        return {
          branchName: branch.label,
          branchCount: matched?.branchCount ?? 0,
          isNotFoundInCDK: matched?.isNotFoundInCDK ?? false
        };
      }

      return {
        branchName: branch.label,
        branchCount: 0,
        isNotFoundInCDK: false
      };
    });

    setBranchState(result);
  }, [availabilityState, branches, mainIndex, rowIndex]);

  const handleSearch = () => {
    if (!brandCode || !partNumber) return;

    refetchPartRequestsPartsCheckOnStock();
  };

  const allAreNotFoundInCDK = branchState.every((branch) => branch.isNotFoundInCDK === true);

  return (
    <div className={styles.listContainer}>
      <div className={styles.header}>
        <div>Branches</div>
        {!isDeleted && isEditMode && (
          <button
            type="button"
            onClick={handleSearch}
            className="btn btn-primary btn-sm text-nowrap"
            disabled={!isEnable || isLoadingPartsLookup || isLoadingBranch}
          >
            <InlineSvg path="/media/icons/custom/zoom_1.svg" className="svg-icon-2" />
            Check on Stock
          </button>
        )}
      </div>

      {isLoadingPartsLookup ? (
        <div>
          <Skeleton count={1} height={'40px'} width={'150px'} style={{ marginBottom: '3px' }} />
        </div>
      ) : (
        <div className={styles.listTable}>
          {branchState.length > 1 && allAreNotFoundInCDK && (
            <div className="fw-bold text-danger small">PART NOT FOUND</div>
          )}
          {branchState?.map((x) => (
            <Fragment key={x.branchName}>
              <div className={clsx(styles.listTableRow, x.branchCount > 0 ? styles.rowOk : null)}>
                <div>
                  {x.branchCount > 0 ? (
                    <InlineSvg path="/media/icons/custom/check-circle-2.svg" className="me-1" />
                  ) : (
                    <InlineSvg path="/media/icons/custom/red-cross-square.svg" className="me-1" />
                  )}
                  {x.branchName}
                </div>
                <div className={clsx(styles.rowValue)}>
                  {x.isNotFoundInCDK === true ? (
                    <InlineSvg path="/media/icons/custom/red-cross.svg" svgClassName="mh-10px" />
                  ) : x.branchCount > 0 ? (
                    x.branchCount
                  ) : (
                    0
                  )}
                </div>
              </div>
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
};
